import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import config from '../../config';
import LogoutRequestHandler from '../Auth/LogoutRequestHandler';

/**
 * @typedef {Object} DataContext
 * @property {string} viewType - e.g., "table", "chart"
 * @property {Object.<string, any>} filters - Active filters
 * @property {string[]} selectedAssets - Asset IDs
 * @property {{start: string, end: string}} [dateRange] - Optional date range
 * @property {string} description - Human-readable description
 */

/**
 * @typedef {Object} Message
 * @property {string} id - Unique message ID
 * @property {'user' | 'assistant'} role - Message sender
 * @property {string} content - Message content
 * @property {Date} timestamp - When the message was sent
 * @property {DataContext} [attachedContext] - Optional attached data context
 * @property {boolean} [isError] - Whether this is an error message
 */

/**
 * @typedef {Object} ChatState
 * @property {boolean} isOpen - Whether the chat panel is open
 * @property {Message[]} messages - Chat message history
 * @property {string} inputValue - Current input value
 * @property {boolean} isLoading - Whether waiting for response
 * @property {DataContext | null} currentContext - Current data view context
 * @property {boolean} hasUnreadMessages - Whether there are unread messages
 * @property {string | null} conversationId - Current conversation ID
 */

const ChatContext = createContext(null);

/**
 * Generate a unique ID
 * @returns {string}
 */
const generateId = () => {
  return `msg_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Generate a conversation ID
 * @returns {string}
 */
const generateConversationId = () => {
  return `conv_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Chat Provider Component
 */
export const ChatProvider = ({ children, initialContext = null }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentContext, setCurrentContext] = useState(initialContext);
  const [hasUnreadMessages, setHasUnreadMessages] = useState(false);
  const [conversationId, setConversationId] = useState(null);
  const [attachedContext, setAttachedContext] = useState(null);
  const [error, setError] = useState(null);

  // Load conversation ID from localStorage on mount
  useEffect(() => {
    const savedConversationId = localStorage.getItem('chat_conversation_id');
    if (savedConversationId) {
      setConversationId(savedConversationId);
    }
  }, []);

  // Clear unread when panel opens
  useEffect(() => {
    if (isOpen) {
      setHasUnreadMessages(false);
    }
  }, [isOpen]);

  /**
   * Get or create conversation ID
   */
  const getConversationId = useCallback(() => {
    if (conversationId) return conversationId;
    
    const newId = generateConversationId();
    setConversationId(newId);
    localStorage.setItem('chat_conversation_id', newId);
    return newId;
  }, [conversationId]);

  /**
   * Toggle chat panel open/closed
   */
  const togglePanel = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  /**
   * Open chat panel
   */
  const openPanel = useCallback(() => {
    setIsOpen(true);
  }, []);

  /**
   * Close chat panel
   */
  const closePanel = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Add a message to the chat
   * @param {Message} message
   */
  const addMessage = useCallback((message) => {
    setMessages(prev => [...prev, message]);
    if (!isOpen && message.role === 'assistant') {
      setHasUnreadMessages(true);
    }
  }, [isOpen]);

  /**
   * Clear all messages
   */
  const clearMessages = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    localStorage.removeItem('chat_conversation_id');
  }, []);

  /**
   * Update data context
   * @param {DataContext} context
   */
  const updateContext = useCallback((context) => {
    setCurrentContext(context);
  }, []);

  /**
   * Attach current context to next message
   */
  const attachContext = useCallback(() => {
    if (currentContext) {
      setAttachedContext(currentContext);
    }
  }, [currentContext]);

  /**
   * Remove attached context
   */
  const removeAttachedContext = useCallback(() => {
    setAttachedContext(null);
  }, []);

  /**
   * Send a message to the chat API
   * @param {string} content - Message content
   */
  const sendMessage = useCallback(async (content) => {
    if (!content || content.trim().length === 0) {
      setError('Message cannot be empty');
      return;
    }

    if (content.length > 10000) {
      setError('Message is too long (max 10,000 characters)');
      return;
    }

    setError(null);

    // Create user message for immediate display
    const userMessage = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
      attachedContext: attachedContext
    };

    // Add user message (optimistic update)
    addMessage(userMessage);
    setInputValue('');
    setIsLoading(true);

    // Clear attached context after sending
    const contextToSend = attachedContext;
    setAttachedContext(null);

    try {
      // Prepare request payload
      const payload = {
        message: content.trim(),
        conversation_id: getConversationId(),
      };

      // Include context if attached
      if (contextToSend) {
        payload.context = {
          viewType: contextToSend.viewType,
          filters: contextToSend.filters,
          selectedAssets: contextToSend.selectedAssets,
          dateRange: contextToSend.dateRange,
          description: contextToSend.description
        };
      }

      // Get CSRF token if available
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

      // Send to backend
      const response = await fetch(`${config.BackendUrl}agent/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-TOKEN': csrfToken,
          'Authorization': `Bearer ${localStorage.getItem('token') || ''}`
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        if (response.status === 401) {
          LogoutRequestHandler();
          return;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Request failed with status ${response.status}`);
      }

      const responseJson = await response.json();
      const data = responseJson?.data ?? responseJson;
      console.log('Chat response data:', data);

      // Create assistant message from response
      const assistantMessage = {
        id: data.message_id || generateId(),
        role: 'assistant',
        content: data.response || data.message || 'I received your message.',
        timestamp: new Date()
      };

      console.log('Assistant message:', assistantMessage);

      addMessage(assistantMessage);

      // Update conversation ID if backend returns a new one
      if (data.conversation_id) {
        setConversationId(data.conversation_id);
        localStorage.setItem('chat_conversation_id', data.conversation_id);
      }

    } catch (err) {
      console.error('Chat error:', err);
      
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message. Please try again.';
      setError(errorMessage);

      // Add error message to chat
      addMessage({
        id: generateId(),
        role: 'assistant',
        content: `Error: ${errorMessage}`,
        timestamp: new Date(),
        isError: true
      });

    } finally {
      setIsLoading(false);
    }
  }, [addMessage, attachedContext, getConversationId]);

  /**
   * Start a new conversation
   */
  const startNewConversation = useCallback(() => {
    clearMessages();
    const newId = generateConversationId();
    setConversationId(newId);
    localStorage.setItem('chat_conversation_id', newId);
  }, [clearMessages]);

  const value = {
    // State
    isOpen,
    messages,
    inputValue,
    isLoading,
    currentContext,
    hasUnreadMessages,
    conversationId,
    attachedContext,
    error,
    
    // Actions
    setInputValue,
    togglePanel,
    openPanel,
    closePanel,
    sendMessage,
    addMessage,
    clearMessages,
    updateContext,
    attachContext,
    removeAttachedContext,
    startNewConversation,
    setError
  };

  return (
    <ChatContext.Provider value={value}>
      {children}
    </ChatContext.Provider>
  );
};

/**
 * Hook to use chat context
 * @returns {Object} Chat context value
 */
export const useChat = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChat must be used within a ChatProvider');
  }
  return context;
};

/**
 * Hook for auto-scrolling chat messages
 * @param {Array} messages - Messages array
 * @param {boolean} isLoading - Loading state
 * @returns {Object} Refs and scroll functions
 */
export const useAutoScroll = (messages, isLoading) => {
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [showScrollButton, setShowScrollButton] = useState(false);

  const scrollToBottom = useCallback((behavior = 'smooth') => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  }, []);

  // Auto-scroll on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading, scrollToBottom]);

  // Check if user scrolled up
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;
    
    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    setShowScrollButton(!isNearBottom);
  }, []);

  return {
    messagesEndRef,
    messagesContainerRef,
    showScrollButton,
    scrollToBottom,
    handleScroll
  };
};

/**
 * Hook for textarea auto-resize
 * @param {string} value - Input value
 * @returns {Object} Ref and resize function
 */
export const useAutoResize = (value) => {
  const textareaRef = useRef(null);

  useEffect(() => {
    const textarea = textareaRef.current;
    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [value]);

  return { textareaRef };
};

export default ChatContext;
